/**
 * Tenant Context - Migrated to use API endpoints
 * Uses Cloudflare Workers API
 */

import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import { appConfig } from '@/config/environment';

// Sentinel UUID for default/unresolved tenant - valid UUID format for database compatibility
export const DEFAULT_TENANT_ID = '00000000-0000-0000-0000-000000000000';

export interface TenantConfig {
  id: string;
  domain: string;
  subdomain?: string;
  name: string;
  slug: string;
  isPrimary: boolean;
  isActive: boolean;
  apiEndpoint: string;
  customConfig: Record<string, unknown>;
  brandConfig?: {
    primaryColor: string;
    logo?: string;
    theme: string;
  };
}

interface TenantContextValue {
  tenant: TenantConfig | null;
  loading: boolean;
  error: string | null;
  switchTenant: (tenantId: string) => Promise<void>;
  refreshTenant: () => Promise<void>;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

interface TenantProviderProps {
  children: React.ReactNode;
}

const detectTenantFromUrl = (): { domain: string; subdomain?: string } => {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  if (parts.length === 3) {
    return {
      domain: `${parts[1]}.${parts[2]}`,
      subdomain: parts[0],
    };
  } else if (parts.length === 2) {
    return {
      domain: hostname,
    };
  } else {
    return {
      domain: hostname,
    };
  }
};

const getDefaultTenant = (domain: string): TenantConfig => ({
  id: DEFAULT_TENANT_ID,
  domain: domain,
  name: 'LegalSyncUp',
  slug: 'default',
  isPrimary: true,
  isActive: true,
  apiEndpoint: appConfig.API_URL || '',
  customConfig: {},
  brandConfig: {
    primaryColor: '#2563eb',
    theme: 'swiss',
  },
});

const resolveTenant = async (): Promise<TenantConfig | null> => {
  try {
    const { domain, subdomain } = detectTenantFromUrl();

    if (domain === 'localhost' || domain === '127.0.0.1') {
      return getDefaultTenant(domain);
    }

    if (domain.endsWith('workers.dev')) {
      return getDefaultTenant(domain);
    }

    if (domain === 'legalsyncup.com' || domain.endsWith('.legalsyncup.com')) {
      return getDefaultTenant(domain);
    }

    try {
      const apiUrl = `${appConfig.API_URL || ''}/api/tenant/resolve`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ domain, subdomain }),
      });

      if (!response.ok) {
        console.warn('Tenant resolution API failed, using default tenant');
        return getDefaultTenant(domain);
      }

      const tenantData = (await response.json()) as Record<string, unknown>;
      return (tenantData.data ?? tenantData) as TenantConfig;
    } catch (apiError) {
      console.warn('Tenant resolution API error, using default tenant:', apiError);
      return getDefaultTenant(domain);
    }
  } catch (err) {
    console.error('Tenant resolution error:', err);
    return getDefaultTenant(window.location.hostname);
  }
};

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTenant = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const tenantConfig = await resolveTenant();
      setTenant(tenantConfig);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tenant');
    } finally {
      setLoading(false);
    }
  }, []);

  const switchTenant = async (tenantId: string) => {
    // Implementation for tenant switching
    setLoading(true);
    try {
      // This would redirect to the tenant's domain
      window.location.href = `https://${tenantId}.legalsyncup.com`;
    } catch (err) {
      setError('Failed to switch tenant');
      setLoading(false);
    }
  };

  const refreshTenant = async () => {
    await loadTenant();
  };

  useEffect(() => {
    loadTenant();
  }, [loadTenant]);

  const value: TenantContextValue = {
    tenant,
    loading,
    error,
    switchTenant,
    refreshTenant,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};
